import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  useActiveProjectId,
  useIsResultCategoryRegistryEnabled,
  useServiceAuth,
} from '../../../../hooks';
import MedicalResultsService, {
  MEDICAL_RESULTS_SYSTEM,
  MedicalResultSource,
  ResultCategory,
} from '../../../../services/MedicalResultsService';

export type LabResultsConfigItem = {
  title: string;
  coding?: Required<Pick<fhir.Coding, 'system' | 'code'>>;
  source?: MedicalResultSource;
};

type LabResultsConfig = LabResultsConfigItem[];

const SERVICE = new MedicalResultsService();

const SOURCE_SECTIONS: LabResultsConfigItem[] = [
  { title: 'Uncategorized', source: 'unmappedLabs' as const },
  { title: 'Bring Your Own Health Data', source: 'memberUpload' as const },
  { title: 'Extracted Results', source: 'documentExtractionV2' as const },
];

// Hardcoded PHC Lab Results sections. Used while resultCategoryRegistry is off,
// and as a 404 escape hatch if the registry GET is missing in an environment.
const FALLBACK_CODING_SECTIONS: LabResultsConfigItem[] = [
  {
    title: 'Cardiovascular',
    coding: { code: 'cardiovascular', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Metabolic',
    coding: { code: 'metabolic', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Hormones',
    coding: { code: 'hormones', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Thyroid',
    coding: { code: 'thyroid', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Inflammation',
    coding: { code: 'inflammation', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Nutrients',
    coding: { code: 'nutrients', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Immune',
    coding: { code: 'immune', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Blood',
    coding: { code: 'blood', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Kidney',
    coding: { code: 'kidney', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Liver',
    coding: { code: 'liver', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Prostate',
    coding: { code: 'prostate', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'GI Health',
    coding: { code: 'gi-health', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Cancer Markers',
    coding: { code: 'cancer-markers', system: MEDICAL_RESULTS_SYSTEM },
  },
  {
    title: 'Musculoskeletal',
    coding: { code: 'musculoskeletal', system: MEDICAL_RESULTS_SYSTEM },
  },
];

export const labResultsCategoriesQueryKey = (projectId: string) =>
  ['lab-results-config', 'result-categories', projectId] as const;

const codingSectionsFromRegistry = (
  items: ResultCategory[],
): LabResultsConfigItem[] =>
  items.map(item => ({
    title: item.display,
    coding: {
      code: item.code,
      system: MEDICAL_RESULTS_SYSTEM,
    },
  }));

const withSourceSections = (
  codingSections: LabResultsConfigItem[],
): LabResultsConfig => [...codingSections, ...SOURCE_SECTIONS];

export const useLabResultsConfig = (): LabResultsConfig => {
  const projectId = useActiveProjectId();
  const serviceAuth = useServiceAuth();
  const registryEnabled = useIsResultCategoryRegistryEnabled();

  const query = useQuery({
    queryKey: labResultsCategoriesQueryKey(projectId),
    enabled: registryEnabled && Boolean(projectId),
    queryFn: async () => {
      try {
        const response = await SERVICE.getResultCategories(serviceAuth)({
          projectId,
          usedOnly: true,
        });
        return { items: response.data.items, useFallback: false as const };
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          return { items: [] as ResultCategory[], useFallback: true as const };
        }
        throw error;
      }
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  if (!registryEnabled || query.data?.useFallback) {
    return withSourceSections(FALLBACK_CODING_SECTIONS);
  }

  if (!query.data) {
    return withSourceSections([]);
  }

  return withSourceSections(codingSectionsFromRegistry(query.data.items));
};
